import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { IconBell, IconBellRinging, IconChecklist, IconChevronDown, IconClipboardCheck, IconClock, IconDots, IconFileText, IconCircleCheck, IconLock, IconPlus, IconSearch, IconSend, IconTrash, IconX, IconAlertTriangle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { filterBl, isBlocked, tabCounts } from './logic';
import type { BillType, BlDraft, BlTab, DraftResp } from './types';
import { buildTab, TabRows } from './BlTables';
import { BlDock } from './BlDock';
import { BlCompact } from './BlCompact';
import s from './bl.module.css';

// Mint-BL (desktop 1536): tab nhóm B/L · tìm + Lọc · cảnh báo B/L gửi ngoài luồng · bảng "Draft chờ khách". Chỉ đọc; nút ghi báo toast.
// 6 tab còn lại dựng ở BlTables.tsx. Bấm dòng draft / final / release → trang chi tiết 2:1 ở BlDetailPage.tsx. Dock phải (≥ 1800) ở BlDock.tsx. Số tiền ẩn (money = hide).

const MAIN: readonly BlTab[] = ['draft', 'final', 'rel', 'amd'];
const LABEL: Record<BlTab, string> = { draft: 'Draft chờ khách', final: 'Final cần gửi', rel: 'Release', amd: 'Sửa sau phát hành', loi: 'Mẫu LOI theo hãng', orig: 'Chứng từ gốc', mnf: 'Manifest / AMS' };
const MORE: readonly BlTab[] = ['loi', 'orig', 'mnf'];
const F_TYPES: readonly BillType[] = ['Original', 'Surrender', 'Seaway'];
const F_CARS = ['YML', 'ONE', 'CNC', 'Evergreen', 'SITC', 'KMTC', 'Heung-A', 'Namsung'] as const;

const TYPE_TONE = { Original: 'grey', Surrender: 'violet', Seaway: 'green', Telex: 'grey' } as const;
const RESP: Record<DraftResp, { k: string; tone: string }> = {
  none: { k: 'Chưa gửi', tone: 'grey' },
  wait: { k: 'Chờ xác nhận', tone: 'amber' },
  fix: { k: 'Yêu cầu sửa', tone: 'red' },
  ok: { k: 'Đã xác nhận', tone: 'green' },
};

/** Rộng cột theo board (tổng 1264, quy về CW 1240): job 200 · khách 220 · hãng 150 · LOI 130 · phiên bản 170 · phản hồi 150 · hạn 118 · nút 126. */
const COLS = [
  { key: 'job', header: 'Job · HBL', w: 200 },
  { key: 'cus', header: 'Khách · Consignee', w: 220 },
  { key: 'car', header: 'Hãng · Loại bill', w: 150 },
  { key: 'loi', header: 'LOI', w: 130 },
  { key: 'ver', header: 'Phiên bản · Gửi lúc', w: 170 },
  { key: 'resp', header: 'Phản hồi khách', w: 150 },
  { key: 'due', header: 'Hạn SI', w: 118 },
  { key: 'act', header: '', w: 126 },
] as const;
const pct = (w: number, total: number) => `${((w / total) * 100).toFixed(3)}%`;

interface TabItem { key: BlTab; label: string; n?: number }

/** Nhóm tab (board `TABS`): 4 tab chính + "Thêm ▾"; số đếm xám, xanh ở tab đang chọn. */
function BlTabs({ value, main, more, onChange }: { value: BlTab; main: readonly TabItem[]; more: readonly TabItem[]; onChange: (k: BlTab) => void }): ReactNode {
  const [open, setOpen] = useState(false);
  const inMore = more.find((x) => x.key === value);
  const pick = (k: BlTab) => {
    onChange(k);
    setOpen(false);
  };
  return (
    <div className={s.seg} role="tablist" aria-label="Nhóm việc B/L">
      {main.map((x) => (
        <button key={x.key} type="button" role="tab" className={s.tab} aria-selected={value === x.key} onClick={() => pick(x.key)}>
          {x.label}
          {x.n !== undefined && <span className={s.tabN}>{x.n}</span>}
        </button>
      ))}
      <span className={s.wrap}>
        <button type="button" className={s.tab} aria-haspopup="menu" aria-expanded={open} aria-pressed={!!inMore} onClick={() => setOpen(!open)}>
          {inMore ? inMore.label : 'Thêm'}
          <IconChevronDown size={14} aria-hidden="true" />
        </button>
        {open && (
          <div className={s.menu} role="menu" aria-label="Nhóm B/L khác">
            {more.map((x) => (
              <button key={x.key} type="button" role="menuitemradio" aria-checked={value === x.key} className={s.item} onClick={() => pick(x.key)}>
                {x.label}
                {x.n !== undefined && <span className={s.tabN}>{x.n}</span>}
              </button>
            ))}
          </div>
        )}
      </span>
    </div>
  );
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function FChip({ k, onRemove }: { k: string; onRemove: () => void }) {
  return (
    <span className={s.fchip}>
      {k}
      <button type="button" aria-label={`Bỏ lọc ${k}`} onClick={onRemove}>
        <IconX size={14} aria-hidden="true" />
      </button>
    </span>
  );
}

function Pill({ tone, title, children }: { tone: string; title?: string; children: string }) {
  return (
    <span className={`${s.pill} ${s[`p_${tone}`]}`} title={title}>
      <span className={s.dot} aria-hidden="true" />
      {children}
    </span>
  );
}

function DraftRow({ r, onOpen, onAct }: { r: BlDraft; onOpen: (id: string) => void; onAct: (msg: string) => void }) {
  const t = useT();
  const block = isBlocked(r);
  const [, dueDay, ...dueRest] = r.due.split(' ');
  const act =
    r.resp === 'ok'
      ? { k: 'Đã xác nhận', cls: s.a_done, dis: true, tip: '', ic: <IconCircleCheck size={16} aria-hidden="true" /> }
      : block
        ? { k: 'Thiếu LOI', cls: s.a_dis, dis: true, ic: <IconBellRinging size={16} aria-hidden="true" />, tip: r.resp === 'fix' ? 'Tạo LOI trước khi gửi bản sửa' : `Bill ${r.type} cần LOI đúng mẫu hãng ${r.car}` }
        : r.resp === 'wait'
          ? { k: 'Nhắc khách', cls: s.a_ghost, dis: false, tip: '', ic: <IconBellRinging size={16} aria-hidden="true" /> }
          : { k: r.resp === 'fix' ? `Gửi v${r.ver + 1}` : 'Gửi Draft', cls: s.a_pri, dis: false, tip: '', ic: <IconSend size={16} aria-hidden="true" /> };
  return (
    <tr onClick={() => onOpen(r.id)}>
      <td>
        <div className={s.link}>{r.job}</div>
        <div className={s.sub}>{r.hbl}</div>
      </td>
      <td>
        <div className={s.main}>{r.cus}</div>
        <div className={s.sub}>{r.cne}</div>
      </td>
      <td>
        <Pill tone={TYPE_TONE[r.type]}>{r.type}</Pill>
        <div className={s.sub} title={`${r.car} · nguồn ${r.src}`}>
          {r.car} · nguồn {r.src}
        </div>
      </td>
      <td>
        {r.loi === 'missing' ? (
          <>
            <div className={`${s.main} ${s.err}`}>Thiếu LOI {r.car}</div>
            <div className={s.sub}>{t('bl.blockSend')}</div>
          </>
        ) : r.loi === 'ok' ? (
          <>
            <div className={`${s.main} ${s.ok}`}>{t('bl.attached')}</div>
            <div className={s.sub}>mẫu {r.car}</div>
          </>
        ) : (
          <div className={s.lb}>{t('bl.notNeeded')}</div>
        )}
      </td>
      <td>
        <div>{r.ver ? `v${r.ver}` : '—'}</div>
        <div className={s.sub}>{r.sent}</div>
      </td>
      <td>
        <Pill tone={RESP[r.resp].tone} title={r.resp === 'fix' ? r.fix : undefined}>
          {RESP[r.resp].k}
        </Pill>
        {r.resp === 'fix' && r.fix ? <div className={s.sub}>{r.fix}</div> : null}
      </td>
      <td title={`${r.due} · ${r.resp === 'ok' ? 'đã xác nhận' : (r.wh ?? '')}`}>
        <div>{`${dueDay} ${dueRest.join(' ')}`}</div>
        <div className={`${s.sub} ${r.whWarn && r.resp !== 'ok' ? s.warnTone : ''}`}>
          {r.resp === 'ok' ? <IconCircleCheck size={12} stroke={2} aria-hidden="true" /> : r.wh ? <IconClock size={12} stroke={2} aria-hidden="true" /> : null}
          <span>{r.resp === 'ok' ? 'Xong' : (r.wh ?? '').replace(/^còn /, '').replace(/ giờ làm việc$/, '')}</span>
        </div>
      </td>
      <td className={s.end}>
        <button
          type="button"
          className={`${s.act} ${act.cls}`}
          disabled={act.dis}
          title={act.tip || undefined}
          onClick={(e) => {
            e.stopPropagation();
            onAct(act.k);
          }}
        >
          {act.ic}
          {act.k}
        </button>
      </td>
    </tr>
  );
}

function BlTable() {
  const t = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('blDraft', { limit: 5000 });
  const fin = useList('blFinal', { limit: 5000 });
  const rel = useList('blRelease', { limit: 5000 });
  const amd = useList('blAmend', { limit: 5000 });
  const loi = useList('blLoi', { limit: 5000 });
  const orig = useList('blOriginal', { limit: 5000 });
  const mnf = useList('blManifest', { limit: 5000 });
  const forced = readState();
  const [tab, setTab] = useState<BlTab>('draft');
  const [q, setQ] = useState('');
  const [warn, setWarn] = useState(true);
  const [fOpen, setFOpen] = useState(false);
  const navigate = useNavigate();
  const [dock, setDock] = useState(true);
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  const [fT, setFT] = useState<readonly BillType[]>([]);
  const [fC, setFC] = useState<readonly string[]>([]);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const toggle = <T,>(a: readonly T[], k: T): T[] => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);

  const all = data?.items ?? [];
  const rowsDraft = useMemo(() => filterBl(all, { q, types: fT, cars: fC }), [all, q, fT, fC]);
  const other = useMemo(
    () => (tab === 'draft' ? null : buildTab(tab, { finals: fin.data?.items ?? [], releases: rel.data?.items ?? [], amends: amd.data?.items ?? [], lois: loi.data?.items ?? [], originals: orig.data?.items ?? [], manifests: mnf.data?.items ?? [] }, q)),
    [tab, q, fin.data, rel.data, amd.data, loi.data, orig.data, mnf.data],
  );
  const openRec = (id: string) => {
    const has = (xs?: readonly { id: string }[]) => !!xs?.some((x) => x.id === id);
    if (has(all) || has(fin.data?.items) || has(rel.data?.items) || has(amd.data?.items) || has(loi.data?.items) || has(orig.data?.items) || has(mnf.data?.items)) void navigate({ to: '/ezy/bl/$id', params: { id } });
    else toast({ msg: 'Chi tiết B/L chưa mở ở bản này' });
  };
  const rows = other ? other.rows : rowsDraft;
  const counts = tabCounts({
    drafts: all,
    finals: fin.data?.items ?? [],
    releases: rel.data?.items ?? [],
    amends: amd.data?.items ?? [],
    lois: loi.data?.items ?? [],
    originals: orig.data?.items ?? [],
    manifests: mnf.data?.items ?? [],
  });

  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const pickTab = (k: BlTab) => setTab(k);

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (tab === 'draft' && all.length === 0) state = 'empty';
  const ready = state === 'ready' || state === 'noperm';
  const cols = other ? other.cols.map(([header, w]) => ({ header, w })) : COLS;
  const total = cols.reduce((a, c) => a + c.w, 0);
  const showDock = wide && dock;
  const warnOn = warn && ready && (tab === 'draft' || tab === 'final');
  const blocked = rowsDraft.filter(isBlocked).length;

  const mk = (k: BlTab) => ({ key: k, label: LABEL[k], n: ready ? counts[k] : undefined });

  return (
    <Page kind="table">
      <PageHeader findPad
        title="Chứng từ & B/L"
        findHint={false}
        tabs={<BlTabs value={tab} main={MAIN.map(mk)} more={MORE.map(mk)} onChange={pickTab} />}
        actions={
          <>
            <HeaderTool label="Việc" icon={IconChecklist} n={5} />
            <HeaderTool label="Duyệt" icon={IconClipboardCheck} n={ready ? 1 : undefined} />
            <HeaderTool label="Thông báo" icon={IconBell} n={3} />
            <HeaderTool label="Thao tác khác" icon={IconDots} onClick={noWrite} />
            <Button variant="primary" icon={tab === 'loi' || tab === 'orig' ? <IconPlus size={16} aria-hidden="true" /> : <IconSend size={16} aria-hidden="true" />} onClick={noWrite}>
              {tab === 'loi' ? 'Thêm mẫu LOI' : tab === 'orig' ? 'Ghi chứng từ gốc' : 'Gửi Draft'}
            </Button>
          </>
        }
      />
      <div className={`${s.body} ${showDock ? s.bodyDock : ''}`}>
      <div className={s.page}>
        <div className={s.tb}>
          <label className={s.search}>
            <IconSearch size={16} aria-hidden="true" />
            <input aria-label="Tìm" placeholder="Job / HBL / MBL / khách / số chứng từ" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          {fT.length > 0 && <FChip k={`Loại: ${fT.join(', ')}`} onRemove={() => setFT([])} />}
          {fC.length > 0 && <FChip k={`Hãng: ${fC.slice(0, 2).join(', ')}${fC.length > 2 ? ` +${fC.length - 2}` : ''}`} onRemove={() => setFC([])} />}
          <span className={s.wrap}>
            <button type="button" className={s.filter} aria-haspopup="dialog" aria-expanded={fOpen} onClick={() => setFOpen(!fOpen)}>
              <IconPlus size={16} aria-hidden="true" />
              {t('bl.filter')}
            </button>
            {fOpen && (
              <div role="dialog" aria-label="Lọc theo loại bill và hãng" className={s.fbox}>
                <div className={s.fh}><b>{t('bl.billType')}</b><span>{t('bl.isOneOf')}</span></div>
                {F_TYPES.map((k) => (
                  <label key={k} className={s.fopt}>
                    <input type="checkbox" checked={fT.includes(k)} onChange={() => setFT(toggle(fT, k))} />
                    {k}
                    <span>{all.filter((r) => r.type === k).length}</span>
                  </label>
                ))}
                <div className={s.fh}><b>{t('bl.carrier')}</b></div>
                {F_CARS.map((k) => (
                  <label key={k} className={s.fopt}>
                    <input type="checkbox" checked={fC.includes(k)} onChange={() => setFC(toggle(fC, k))} />
                    {k}
                    <span>{all.filter((r) => r.car === k).length}</span>
                  </label>
                ))}
                <div className={s.ff}>
                  <span>{rows.length} dòng khớp ở tab {LABEL[tab]}</span>
                  <Button variant="primary" size="sm" icon={<IconCircleCheck size={16} aria-hidden="true" />} onClick={() => setFOpen(false)}>Xong</Button>
                </div>
              </div>
            )}
          </span>
          {(fT.length > 0 || fC.length > 0 || q) && (
            <button type="button" className={s.clr} onClick={() => { setFT([]); setFC([]); setQ(''); }}>
              <IconTrash size={16} aria-hidden="true" />
              {t('bl.clear')}
            </button>
          )}
          {ready && <span className={s.sum}>{rows.length} dòng</span>}
        </div>
        {warnOn && !showDock && (
          <div role="note" className={s.warn}>
            <span className={s.warnIc} aria-hidden="true">
              <IconAlertTriangle size={16} stroke={2} />
            </span>
            <div className={s.warnTx}>
              <b>{t('bl.offFlow1')}</b> <span>{t('bl.offFlowMeta')}</span>
            </div>
            <button type="button" className={s.ack} onClick={noWrite}>
              <IconFileText size={16} aria-hidden="true" />
              {t('bl.logToJob')}
            </button>
            <button type="button" className={s.x} aria-label={t('bl.hideWarn')} onClick={() => setWarn(false)}>
              <IconX size={16} aria-hidden="true" />
            </button>
          </div>
        )}
        {state === 'noperm' && (
          <div role="note" className={s.note}>
            <IconLock size={16} aria-hidden="true" />
            {t('bl.viewOnlyMoney')}
          </div>
        )}
        <StateView
          state={ready && rows.length === 0 ? 'empty' : ready ? 'ready' : state}
          title={ready ? (other ? 'Không có dòng khớp' : 'Không có Draft khớp') : (other?.empty ?? 'Không có Draft nào chờ khách')}
          desc={ready ? 'Thử đổi từ khoá tìm.' : (other ? other.emptySub : 'Mọi Draft trong kỳ đã được khách xác nhận.')}
          icon={<IconFileText size={26} stroke={1.8} />}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('bl.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          <section className={s.card} aria-label={LABEL[tab]}>
            <table className={s.table} aria-label={LABEL[tab]} aria-rowcount={rows.length + 1}>
              <colgroup>
                {cols.map((c, i) => (
                  <col key={i} style={{ width: pct(c.w, total) }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {cols.map((c, i) => (
                    <th key={i} scope="col">
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {other && <TabRows t={other} onOpen={openRec} onAct={noWrite} />}
                {!other && rowsDraft.map((r) => (
                  <DraftRow key={r.id} r={r} onOpen={openRec} onAct={(k) => (k === 'Nhắc khách' ? toast({ msg: `Chưa mở ghi ở bản này · ${r.cus}` }) : noWrite())} />
                ))}
              </tbody>
            </table>
            <div className={s.foot}>{other ? other.foot : `${rowsDraft.length} bill · ${blocked} bị chặn vì thiếu LOI`}</div>
          </section>
        </StateView>
      </div>
      {showDock && <BlDock releases={rel.data?.items ?? []} warn={warnOn} onWarnClose={() => setWarn(false)} onClose={() => setDock(false)} onWrite={noWrite} onOpenRelease={() => setTab('rel')} />}
      </div>
    </Page>
  );
}

export function BlList() {
  const layout = useLayout();
  return layout === 'pad' || layout === 'phone' ? <BlCompact phone={layout === 'phone'} /> : <BlTable />;
}
